import { UserRole } from '@streamr/tv-engine';
import { showPrivateMessageToast } from '../../components/popups/PrivateMessageToast';
import { playRequestNotificationSound } from '../../utils/notificationSound';
import { UserServiceOutput, } from './UserServiceOutput';
const AUTO_ACCEPT = new URLSearchParams(window.location.search).get('autoAccept') === 'true';
export class UserService {
    static instance;
    static getInstance() {
        if (!UserService.instance) {
            UserService.instance = new UserService();
        }
        return UserService.instance;
    }
    static getNotificationInterface() {
        return UserService.getInstance();
    }
    output;
    userManager;
    openPeoplePanelCallback;
    previousIncomingRequestUserIds = new Set();
    constructor() {
        this.output = new UserServiceOutput();
    }
    setOpenPeoplePanelCallback(callback) {
        this.openPeoplePanelCallback = callback;
    }
    clearOpenPeoplePanelCallback() {
        this.openPeoplePanelCallback = undefined;
    }
    async init() {
        // No initialization needed
    }
    static getOutput() {
        return UserServiceOutput.getReactiveOutput();
    }
    setUserManager(userManager) {
        if (this.userManager) {
            this.userManager.off('ownRoleChanged', this.handleUserRoleChanged);
            this.userManager.off('incomingRequestsChanged', this.handleIncomingRequestsChanged);
            this.userManager.off('privateChatMessage', this.handlePrivateChatMessage);
            this.userManager.off('outgoingRequestsChanged', this.handleOutgoingRequestsChanged);
            this.userManager.off('displayNamesChanged', this.handleDisplayNamesChanged);
            this.userManager.off('userWentOffline', this.handleUserWentOffline);
        }
        this.userManager = userManager;
        this.userManager.on('ownRoleChanged', this.handleUserRoleChanged);
        this.userManager.on('incomingRequestsChanged', this.handleIncomingRequestsChanged);
        this.userManager.on('outgoingRequestsChanged', this.handleOutgoingRequestsChanged);
        this.userManager.on('privateChatMessage', this.handlePrivateChatMessage);
        this.userManager.on('displayNamesChanged', this.handleDisplayNamesChanged);
        this.userManager.on('userWentOffline', this.handleUserWentOffline);
        const currentStatus = this.userManager.getCurrentUserRole();
        this.output.setUserRole(currentStatus);
        if (currentStatus === UserRole.COHOST) {
            const incomingRequests = this.userManager.getIncomingRequests();
            this.output.setIncomingRequests(incomingRequests);
            this.previousIncomingRequestUserIds = new Set(incomingRequests.map((req) => req.userId));
        }
        this.updateParticipantLists();
        const localName = this.userManager.getLocalDisplayName();
        if (localName) {
            this.output.setLocalDisplayName(localName);
        }
        const names = this.userManager.getDisplayNames();
        const record = {};
        names.forEach((name, userId) => {
            record[userId] = name;
        });
        this.output.setDisplayNames(record);
    }
    updateParticipantLists() {
        if (this.userManager) {
            this.output.setSpeakers(this.userManager.getSpeakers());
            this.output.setGuests(this.userManager.getGuests());
            this.output.setCameras(this.userManager.getCameras());
        }
    }
    handleUserRoleChanged = (userRole, _oneToOneStreamUrl) => {
        this.output.setUserRole(userRole);
        if (userRole !== UserRole.COHOST) {
            this.output.setIncomingRequests([]);
            this.previousIncomingRequestUserIds.clear();
        }
        this.updateParticipantLists();
    };
    handleIncomingRequestsChanged = (requests) => {
        if (this.userManager) {
            const currentStatus = this.userManager.getCurrentUserRole();
            if (currentStatus === UserRole.COHOST) {
                this.output.setIncomingRequests(requests);
                const currentRequestUserIds = new Set(requests.map((req) => req.userId));
                const newRequestUserIds = [];
                currentRequestUserIds.forEach((userId) => {
                    if (!this.previousIncomingRequestUserIds.has(userId)) {
                        newRequestUserIds.push(userId);
                    }
                });
                if (newRequestUserIds.length > 0) {
                    playRequestNotificationSound();
                    if (AUTO_ACCEPT) {
                        for (const req of requests) {
                            if (newRequestUserIds.includes(req.userId)) {
                                console.log(`[autoAccept] Accepting ${req.userId}`);
                                this.notifyAcceptPermission(req.userId, req.userRole);
                            }
                        }
                    }
                }
                this.previousIncomingRequestUserIds = currentRequestUserIds;
            }
        }
        this.updateParticipantLists();
    };
    handleOutgoingRequestsChanged = (requests) => {
        this.output.setOutgoingRequests(requests);
    };
    handlePrivateChatMessage = (chatMessage) => {
        this.output.addPrivateChatMessage(chatMessage);
        if (!chatMessage.isSent) {
            const senderDisplayName = this.userManager
                ? this.userManager.resolveDisplayName(chatMessage.senderId)
                : undefined;
            showPrivateMessageToast({
                senderId: chatMessage.senderId,
                senderDisplayName,
                message: chatMessage.message,
                onViewClick: () => {
                    this.openPeoplePanelCallback?.();
                },
            });
        }
    };
    handleUserWentOffline = (_userId) => {
        this.updateParticipantLists();
    };
    handleDisplayNamesChanged = (displayNames) => {
        const record = {};
        displayNames.forEach((name, userId) => {
            record[userId] = name;
        });
        this.output.setDisplayNames(record);
        if (this.userManager) {
            const localName = this.userManager.getLocalDisplayName();
            this.output.setLocalDisplayName(localName || '');
        }
    };
    notifySetUserRole(userRole) {
        this.output.setUserRole(userRole);
    }
    notifyRequestPermission(userRole) {
        void this.userManager?.requestPermission(userRole);
    }
    notifyAcceptPermission(userId, userRole) {
        void this.userManager?.acceptPermission(userId, userRole).then(() => {
            this.updateParticipantLists();
        });
    }
    notifyRejectPermission(userId, userRole) {
        this.userManager?.rejectPermission(userId, userRole);
        this.updateParticipantLists();
    }
    notifyLeaveStage() {
        this.userManager?.leaveStage();
    }
    notifyRevokePermission(userId, userRole) {
        this.userManager?.revokePermission(userId, userRole);
        this.updateParticipantLists();
    }
    notifySendPrivateMessage(targetUserId, message) {
        void this.userManager?.sendPrivateMessage(targetUserId, message);
    }
    getCurrentUserId() {
        return this.userManager?.getCurrentUserId();
    }
    notifyAcceptedParticipantTypeChanged(acceptedParticipantType) {
        this.output.setAcceptedParticipantType(acceptedParticipantType);
    }
}
